import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { ROLE_GUIDES, FAQ, ESCALATION_TABLE, CONTACT_SLOTS, HELP_UI, GLOSSARY, FIRST_TIME, pickLang } from "@/lib/helpContent";
import { queueAppIssue, syncAppIssues, listPendingAppIssues } from "@/lib/appIssues";
import { ChevronDown, ChevronRight, Phone, Send, Search, AlertTriangle, WifiOff, Check } from "lucide-react";

const ROLES_WITH_GUIDES = ["inspector", "supervisor", "coordinator", "merchandiser", "director"];

export default function Help() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [tab, setTab] = useState("guide");
  const [showBoth, setShowBoth] = useState(false);

  const rolesToShow = user?.role === "director" ? ROLES_WITH_GUIDES : [user?.role].filter(Boolean);
  const [activeRole, setActiveRole] = useState(rolesToShow[0]);
  useEffect(() => { if (!rolesToShow.includes(activeRole)) setActiveRole(rolesToShow[0]); }, [user?.role]); // eslint-disable-line

  const ui = (k) => pickLang(HELP_UI[k], lang, false).primary;

  return (
    <div className="max-w-[900px] mx-auto pb-24" data-testid="help-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
          {ui("title")}
        </h1>
        <button
          data-testid="help-show-both"
          onClick={() => setShowBoth((v) => !v)}
          className="px-3 py-2 rounded-full text-xs uppercase tracking-widest border border-[#1B3A5C]/20 hover:bg-[#FAF6F0]"
        >
          {showBoth ? ui("hide_both") : ui("show_both")}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#1B3A5C]/10">
        {[
          ["guide", "tab_guide"],
          ["faq", "tab_faq"],
          ["contacts", "tab_contacts"],
        ].map(([id, key]) => (
          <button
            key={id}
            data-testid={`help-tab-${id}`}
            onClick={() => setTab(id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === id ? "text-[#1B3A5C]" : "text-[#7C8E9E] border-transparent"}`}
            style={{ borderColor: tab === id ? "var(--brand-gold)" : "transparent" }}
          >
            {ui(key)}
          </button>
        ))}
      </div>

      {tab === "guide" && (
        <>
          {user?.role === "director" && (
            <div className="flex gap-1 mb-4 flex-wrap" data-testid="help-role-tabs">
              {ROLES_WITH_GUIDES.map((r) => (
                <button
                  key={r}
                  data-testid={`help-role-${r}`}
                  onClick={() => setActiveRole(r)}
                  className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest ${activeRole === r ? "text-white" : "text-[#1B3A5C] border border-[#1B3A5C]/15"}`}
                  style={{ background: activeRole === r ? "var(--brand-navy)" : "transparent" }}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
          <GuideView role={activeRole} lang={lang} showBoth={showBoth} />
        </>
      )}
      {tab === "faq" && <FaqView lang={lang} showBoth={showBoth} placeholder={ui("search_faq")} />}
      {tab === "contacts" && <ContactsView lang={lang} showBoth={showBoth} user={user} />}

      <div className="mt-8">
        <ReportProblem lang={lang} ui={ui} />
      </div>
    </div>
  );
}

/* --------------------------------- GUIDE ---------------------------------- */

function GuideView({ role, lang, showBoth }) {
  const guide = ROLE_GUIDES[role];
  if (!guide) return <div className="text-sm text-[#7C8E9E]">No guide available for role.</div>;
  const title = pickLang(guide.title, lang, showBoth);
  const firstTime = FIRST_TIME[role] || [];
  return (
    <div>
      <div className="mb-4" data-testid="help-guide-title">
        <div className="text-lg font-semibold" style={{ color: "var(--brand-navy)" }}>{title.primary}</div>
        {title.secondary && <div className="text-sm text-[#7C8E9E] mt-1">{title.secondary}</div>}
      </div>

      {firstTime.length > 0 && (
        <div className="mb-4 border border-[#C8A86B]/40 rounded-lg bg-[#FFF8EA] p-4" data-testid={`help-first-time-${role}`}>
          <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--brand-gold)" }}>
            {pickLang(HELP_UI.first_time, lang, false).primary}
          </div>
          <ul className="space-y-2 text-[15px] leading-relaxed list-disc pl-5">
            {firstTime.map((it, i) => {
              const p = pickLang(it, lang, showBoth);
              return (
                <li key={i}>
                  <div>{p.primary}</div>
                  {p.secondary && <div className="text-[13px] text-[#7C8E9E] mt-0.5">{p.secondary}</div>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        {guide.sections.map((s) => <Collapsible key={s.id} section={s} lang={lang} showBoth={showBoth} />)}
      </div>

      {/* Universal glossary — same for every role, adoption critical. */}
      <div className="mt-6" data-testid="help-glossary">
        <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--brand-gold)" }}>
          {pickLang(HELP_UI.glossary, lang, false).primary}
        </div>
        <div className="space-y-3">
          {GLOSSARY.map((s) => <Collapsible key={s.id} section={s} lang={lang} showBoth={showBoth} defaultOpen={false} />)}
        </div>
      </div>
    </div>
  );
}

function Collapsible({ section, lang, showBoth, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const heading = pickLang(section.heading, lang, false);
  return (
    <div className="border border-[#1B3A5C]/10 rounded-lg bg-white" data-testid={`help-section-${section.id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
        data-testid={`help-toggle-${section.id}`}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className="font-semibold text-sm" style={{ color: "var(--brand-navy)" }}>{heading.primary}</span>
      </button>
      {open && (
        <ol className="px-4 pb-4 pl-10 space-y-3 list-decimal text-[15px] leading-relaxed">
          {section.items.map((it, i) => {
            const p = pickLang(it, lang, showBoth);
            return (
              <li key={i}>
                <div>{p.primary}</div>
                {p.secondary && <div className="text-[13px] text-[#7C8E9E] mt-0.5">{p.secondary}</div>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/* ---------------------------------- FAQ ----------------------------------- */

function FaqView({ lang, showBoth, placeholder }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, "");
    const needle = norm(q.trim());
    if (!needle) return FAQ;
    return FAQ.filter((f) => {
      const hay =
        norm(f.q.en) + "|" + norm(f.q.hi) + "|" +
        norm(f.a.en) + "|" + norm(f.a.hi) + "|" +
        (f.keywords || []).map(norm).join("|");
      return hay.includes(needle);
    });
  }, [q]);
  return (
    <div>
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7C8E9E]" />
        <input
          data-testid="help-faq-search"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#1B3A5C]/15 text-sm bg-white"
        />
      </div>
      <div className="space-y-3">
        {filtered.map((f) => <FaqCard key={f.id} f={f} lang={lang} showBoth={showBoth} />)}
        {filtered.length === 0 && <div className="text-sm text-[#7C8E9E]">No match.</div>}
      </div>
    </div>
  );
}

function FaqCard({ f, lang, showBoth }) {
  const [open, setOpen] = useState(false);
  const qp = pickLang(f.q, lang, showBoth);
  const ap = pickLang(f.a, lang, showBoth);
  return (
    <div className="border border-[#1B3A5C]/10 rounded-lg bg-white" data-testid={`help-faq-${f.id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left">
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className="font-semibold text-sm flex-1" style={{ color: "var(--brand-navy)" }}>
          {qp.primary}
          {qp.secondary && <span className="block text-xs text-[#7C8E9E] font-normal mt-0.5">{qp.secondary}</span>}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 pl-10 text-[15px] leading-relaxed">
          <div>{ap.primary}</div>
          {ap.secondary && <div className="text-[13px] text-[#7C8E9E] mt-1">{ap.secondary}</div>}
        </div>
      )}
    </div>
  );
}

/* -------------------------------- CONTACTS -------------------------------- */

function ContactsView({ lang, showBoth, user }) {
  const [contacts, setContacts] = useState({});
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState("");
  const isDirector = user?.role === "director";

  useEffect(() => {
    // Load from localStorage first (works offline), then refresh from server.
    try {
      const cached = JSON.parse(localStorage.getItem("hag_contacts") || "{}");
      setContacts(cached);
    } catch (e) {
      console.debug("[Help] contacts cache read failed:", e?.message);
    }
    if (navigator.onLine) {
      api.get("/settings/contacts").then((r) => {
        setContacts(r.data || {});
        localStorage.setItem("hag_contacts", JSON.stringify(r.data || {}));
      }).catch((e) => {
        console.debug("[Help] contacts fetch failed:", e?.message);
      });
    }
  }, []);

  const save = async (key) => {
    const phone = draft.trim();
    try {
      const r = await api.patch("/settings/contacts", { [key]: phone });
      setContacts(r.data);
      localStorage.setItem("hag_contacts", JSON.stringify(r.data));
      setEditKey(null);
    } catch (e) {
      console.error("[Help] save contact failed:", e?.response?.data || e?.message);
    }
  };

  const uiLabel = (k) => (lang === "en" ? { situation: "Situation", call: "Who to call", when: "When", edit: "Edit", save: "Save", not_set: "Not set" }
                                        : { situation: "स्थिति", call: "किसे कॉल करें", when: "कब", edit: "बदलें", save: "सहेजें", not_set: "सेट नहीं" })[k];

  return (
    <div className="space-y-6">
      {/* Editable slots */}
      <div className="bg-white border border-[#1B3A5C]/10 rounded-lg overflow-hidden" data-testid="help-contact-slots">
        <div className="px-4 py-2.5 text-[11px] uppercase tracking-widest text-[#7C8E9E] border-b border-[#1B3A5C]/10">
          {lang === "en" ? "Quick call" : "त्वरित कॉल"}
        </div>
        {CONTACT_SLOTS.map((c) => {
          const phone = contacts[c.key] || "";
          const label = pickLang(c.label, lang, showBoth);
          const isEditing = editKey === c.key;
          return (
            <div key={c.key} className="px-4 py-3 flex items-center gap-3 border-b border-[#1B3A5C]/5 last:border-b-0" data-testid={`help-contact-${c.key}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold" style={{ color: "var(--brand-navy)" }}>{label.primary}</div>
                {label.secondary && <div className="text-xs text-[#7C8E9E]">{label.secondary}</div>}
              </div>
              {isEditing ? (
                <>
                  <input
                    data-testid={`help-contact-${c.key}-input`}
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="+91…"
                    className="px-3 py-1.5 rounded-md border border-[#1B3A5C]/20 text-sm w-40"
                  />
                  <button data-testid={`help-contact-${c.key}-save`} onClick={() => save(c.key)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold" style={{ background: "var(--brand-gold)", color: "var(--brand-navy)" }}>
                    {uiLabel("save")}
                  </button>
                </>
              ) : (
                <>
                  {phone ? (
                    <a
                      data-testid={`help-contact-${c.key}-call`}
                      href={`tel:${phone}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                      style={{ background: "var(--brand-navy)", color: "white" }}
                    >
                      <Phone size={12} /> {phone}
                    </a>
                  ) : (
                    <span className="text-xs text-[#7C8E9E] italic">{uiLabel("not_set")}</span>
                  )}
                  {isDirector && (
                    <button
                      data-testid={`help-contact-${c.key}-edit`}
                      onClick={() => { setEditKey(c.key); setDraft(phone); }}
                      className="text-xs text-[#7C8E9E] hover:text-[#1B3A5C] underline"
                    >
                      {uiLabel("edit")}
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Escalation table */}
      <div className="bg-white border border-[#1B3A5C]/10 rounded-lg overflow-x-auto" data-testid="help-escalation-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-[#1B3A5C]/10 text-[#7C8E9E] uppercase text-[11px] tracking-widest">
              <th className="px-4 py-2.5">{uiLabel("situation")}</th>
              <th className="px-4 py-2.5">{uiLabel("call")}</th>
              <th className="px-4 py-2.5">{uiLabel("when")}</th>
            </tr>
          </thead>
          <tbody>
            {ESCALATION_TABLE.map((row, i) => {
              const s = pickLang(row.situation, lang, showBoth);
              const c = pickLang(row.call, lang, showBoth);
              const w = pickLang(row.when, lang, showBoth);
              return (
                <tr key={i} className="border-b border-[#1B3A5C]/5 last:border-b-0 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium" style={{ color: "var(--brand-navy)" }}>{s.primary}</div>
                    {s.secondary && <div className="text-xs text-[#7C8E9E]">{s.secondary}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div>{c.primary}</div>
                    {c.secondary && <div className="text-xs text-[#7C8E9E]">{c.secondary}</div>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>{w.primary}</div>
                    {w.secondary && <div className="text-xs text-[#7C8E9E]">{w.secondary}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------------- REPORT A PROBLEM --------------------------- */

function ReportProblem({ lang, ui }) {
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState("");
  const [screenshot, setScreenshot] = useState(null); // data URL
  const [status, setStatus] = useState(null); // "sent" | "queued" | null
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    setPendingCount(listPendingAppIssues().length);
    const onOnline = async () => {
      const r = await syncAppIssues(api);
      setPendingCount(listPendingAppIssues().length);
      if (r.synced > 0) setStatus("sent");
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { alert("Screenshot too large (max 3 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!desc.trim()) return;
    const payload = { description: desc.trim(), screenshot_data_url: screenshot, lang };
    if (navigator.onLine) {
      try {
        await api.post("/escalations/app-issue", payload);
        setStatus("sent");
        setDesc(""); setScreenshot(null); setOpen(false);
        return;
      } catch (e) {
        queueAppIssue(payload);
      }
    } else {
      queueAppIssue(payload);
    }
    setStatus("queued");
    setDesc(""); setScreenshot(null); setOpen(false);
    setPendingCount(listPendingAppIssues().length);
  };

  return (
    <div className="border border-[#1B3A5C]/15 rounded-lg bg-[#FAF6F0] p-4" data-testid="help-report-problem">
      {!open ? (
        <button
          data-testid="help-report-open"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm"
          style={{ background: "var(--brand-navy)", color: "white" }}
        >
          <AlertTriangle size={14} /> {ui("report_problem")}
        </button>
      ) : (
        <div className="space-y-3">
          <textarea
            data-testid="help-report-desc"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder={ui("report_desc")}
            rows={4}
            className="w-full px-3 py-2 rounded-md border border-[#1B3A5C]/15 text-sm bg-white"
          />
          <label className="text-xs text-[#7C8E9E] flex items-center gap-2 cursor-pointer">
            <input data-testid="help-report-screenshot" type="file" accept="image/*" onChange={onFile} className="hidden" />
            <span className="px-3 py-1.5 rounded-md border border-[#1B3A5C]/15 bg-white">{ui("add_screenshot")}</span>
            {screenshot && <Check size={14} className="text-green-600" />}
          </label>
          <div className="flex items-center gap-2">
            <button
              data-testid="help-report-submit"
              onClick={submit}
              disabled={!desc.trim()}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm disabled:opacity-50"
              style={{ background: "var(--brand-gold)", color: "var(--brand-navy)" }}
            >
              <Send size={14} /> {ui("report_submit")}
            </button>
            <button onClick={() => setOpen(false)} className="text-xs text-[#7C8E9E] underline">Cancel</button>
          </div>
        </div>
      )}
      {status === "sent" && (
        <div data-testid="help-report-status-sent" className="mt-3 text-xs text-green-700 flex items-center gap-1"><Check size={12} /> {ui("report_sent")}</div>
      )}
      {status === "queued" && (
        <div data-testid="help-report-status-queued" className="mt-3 text-xs text-amber-700 flex items-center gap-1"><WifiOff size={12} /> {ui("report_queued_offline")}</div>
      )}
      {pendingCount > 0 && (
        <div data-testid="help-report-pending-count" className="mt-2 text-[11px] text-[#7C8E9E]">
          {pendingCount} pending — will send on next online
        </div>
      )}
    </div>
  );
}
